import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'
import { tempTableName } from '../utils'

class TimeSeriesClustering extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.timeseriesclustering',
        group: 'Statistics',
        title: 'Cluster Time Series',
        description: `
        Cluster together time series data based on their similarity using different methods.
        `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'partitioning_col',
          type: WorkflowComponentParamType.Column,
          title: 'Partition column',
          helper: 'Each unique value on this column will be assigned a cluster',
          parent: 'source',
          dataType: [ColumnDataType.String, ColumnDataType.Number]
        },
        {
          name: 'ts_col',
          type: WorkflowComponentParamType.Column,
          title: 'Timestamp column',
          parent: 'source',
          dataType: [ColumnDataType.Datetime, ColumnDataType.Timestamp, ColumnDataType.Date]
        },
        {
          name: 'value_col',
          type: WorkflowComponentParamType.Column,
          title: 'Value column',
          helper: 'Clusters will be calculated based on the value of this column along time',
          parent: 'source',
          dataType: [ColumnDataType.Number]
        },
        {
          name: 'method',
          type: WorkflowComponentParamType.Selection,
          title: 'Algorithm',
          helper: 'Method to compare the time series',
          options: ['Value', 'Profile']
        },
        {
          name: 'n_clusters',
          type: WorkflowComponentParamType.Number,
          title: 'Number of clusters'
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const preparedParams = this._prepareParameters(params)
    const tableName = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const at = connection.quoteName(envVars.analyticsToolboxDataset as string)

    const methodMap = {
      Value: 'value',
      Profile: 'profile'
    }

    const source = connection.quoteName(params.source as string)
    const partitioning_col = preparedParams.partitioning_col as string
    const ts_col = preparedParams.ts_col as string
    const value_col = preparedParams.value_col as string
    const options = {
      method: methodMap[preparedParams.method as string],
      n_clusters: preparedParams.n_clusters as number
    }

    let outTable = tableName
    let query = ''
    let copyFromTempTable = ''

    if (codeType === WorkflowCodeType.StoredProcedure) {
      outTable = `${connection.tempLocation}.${tempTableName(componentName, preparedParams.source)}`
      copyFromTempTable = connection.getCreateTempTableQuery(
        tableName,
        `SELECT * FROM ${connection.quoteName(outTable)}`,
        codeType,
        null
      )
    }

    // Define the provider-specific SQL code
    let multilineStringDelimiters: string[]
    let selectInput: string
    let jsonStringDelimiters: string[]

    if (connection.type === Provider.BigQuery) {
      multilineStringDelimiters = ["'''", "'''"]
      // We need to cast to DATETIME because it's used internally in the procedure
      selectInput = `* EXCEPT (${ts_col}), CAST(${ts_col} AS DATETIME) AS ${ts_col}`
      jsonStringDelimiters = ["JSON '''", "'''"]
    } else if (connection.type === Provider.Snowflake) {
      multilineStringDelimiters = ['$$', '$$']
      // We need to cast to TIMESTAMP because it's used internally in the procedure
      selectInput = `* EXCLUDE (${ts_col}), TO_TIMESTAMP(${ts_col}) AS ${ts_col}`
      jsonStringDelimiters = multilineStringDelimiters
    } else {
      // Unreachable, but silences warnings for not setting variables
      throw new Error(`Provider ${connection.type} not supported by this component`)
    }

    query = dedent`
        CALL ${at}.TIME_SERIES_CLUSTERING(
          ${multilineStringDelimiters[0]}
          SELECT
            ${selectInput}
          FROM ${source}
          ${multilineStringDelimiters[1]},
          '${outTable}',
          '${partitioning_col}',
          '${ts_col}',
          '${value_col}',
          ${jsonStringDelimiters[0]}
          {
            "method": "${options.method}",
            "n_clusters": ${Math.floor(options.n_clusters)}
          }
          ${jsonStringDelimiters[1]}
        );
        ${copyFromTempTable}
      `

    query = connection.wrapCodeInIfTableExistsBlock(query, [tableName], codeType)

    return {
      outputs: { result: connection.quoteName(tableName) },
      query
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    return '1.0.0'
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params }: GetOutputSchemaParams): TableSchema {
    const partitioning_col_name = params.partitioning_col as string
    const partitioning_col = columns.source.find((c) => c.name === partitioning_col_name)
    if (partitioning_col == undefined) {
      return []
    }
    return [
      {
        name: partitioning_col_name,
        type: partitioning_col.type
      },
      {
        name: 'cluster',
        type: ColumnDataType.String
      }
    ]
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake]
  }
}

export { TimeSeriesClustering }
